'use client'

import Link from 'next/link'

import { ContainerInner, ContainerOuter } from '@/components/Container'
import { ThemeToggle } from '@/components/Header'
import { GitHubIcon, LinkedInIcon, XIcon } from '@/components/SocialIcons'
import { SOCIAL_URLS } from '@/lib/contact'
import { currentYear } from '@/lib/site'
import { PRIMARY_NAV_VISIBLE } from '@/lib/nav'
import { CenterUnderline } from '@/components/ui/underline-animation'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useIsClientMounted } from '@/hooks/useIsClientMounted'

const todayFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric',
})

function formatToday() {
  return todayFormatter.format(new Date())
}

// Client-only so the date is the viewer's own day and can't mismatch the
// server (UTC) render on hydration.
function TodayLine() {
  const mounted = useIsClientMounted()
  return (
    <p className="mt-4 max-w-xs text-center text-sm text-zinc-500 md:mt-0 md:text-right dark:text-zinc-400">
      {mounted && `Today is ${formatToday()}. `}This is the website of Arne
      Noori, who thanks you for visiting.
    </p>
  )
}

function NavLink({
  href,
  children,
}: {
  href: string
  children: React.ReactNode
}) {
  return (
    <CenterUnderline
      label={children as string}
      href={href}
      className="text-sm font-medium text-zinc-800 dark:text-zinc-200"
    />
  )
}

function PhotosLink() {
  const { track } = useAnalytics()

  return (
    <CenterUnderline
      label="Photos"
      href="https://photos.arne.ai"
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        track('social_link_click', { platform: 'photos', location: 'footer' })
      }
      className="text-sm font-medium text-zinc-800 dark:text-zinc-200"
    />
  )
}

function SocialLinks({ location }: { location: 'header' | 'footer' | 'home' }) {
  const { track } = useAnalytics()

  const handleSocialClick = (platform: 'twitter' | 'github' | 'linkedin') => {
    track('social_link_click', { platform, location })
  }

  return (
    <div className="mt-2 flex justify-center gap-8">
      <Link
        href={SOCIAL_URLS.twitter}
        className="ease-out-strong -m-3 inline-block p-3 text-zinc-400 transition-[color,transform] duration-200 hover:-translate-y-0.5 hover:text-zinc-600 active:scale-90 dark:hover:text-zinc-200"
        onClick={() => handleSocialClick('twitter')}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow on X (Twitter)"
      >
        <span className="sr-only">X (Twitter)</span>
        <XIcon className="size-5" />
      </Link>
      <Link
        href={SOCIAL_URLS.github}
        className="ease-out-strong -m-3 inline-block p-3 text-zinc-400 transition-[color,transform] duration-200 hover:-translate-y-0.5 hover:text-zinc-600 active:scale-90 dark:hover:text-zinc-200"
        onClick={() => handleSocialClick('github')}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow on GitHub"
      >
        <span className="sr-only">GitHub</span>
        <GitHubIcon className="size-5" />
      </Link>
      <Link
        href={SOCIAL_URLS.linkedin}
        className="ease-out-strong -m-3 inline-block p-3 text-zinc-400 transition-[color,transform] duration-200 hover:-translate-y-0.5 hover:text-zinc-600 active:scale-90 dark:hover:text-zinc-200"
        onClick={() => handleSocialClick('linkedin')}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow on LinkedIn"
      >
        <span className="sr-only">LinkedIn</span>
        <LinkedInIcon className="size-5" />
      </Link>
    </div>
  )
}

export function Footer() {
  // The footer is hidden on immersive routes via CSS (those pages render
  // <HideSiteChrome/>), and the home-only theme toggle is gated via CSS too —
  // both avoid the usePathname() prerender mismatch that flashed the footer.
  return (
    <footer className="site-footer mt-32 flex-none">
      <ContainerOuter>
        <div className="border-t border-zinc-100 pt-10 pb-16 dark:border-zinc-700/40">
          <ContainerInner>
            {/* Large name and slogan - moved to top */}
            <div className="mb-12 flex w-full items-center justify-center">
              <div
                className="text-center text-4xl font-bold text-zinc-900 select-none md:text-6xl lg:text-[7rem] dark:text-zinc-100"
                style={{ fontFamily: 'var(--font-crimson-pro), serif' }}
                role="presentation"
                aria-hidden="true"
              >
                Build with obsession
              </div>
            </div>

            <div className="flex flex-col items-center justify-between gap-8">
              {/* Nav links and personal text in one row */}
              <div className="flex w-full flex-col items-center justify-between gap-6 md:flex-row">
                <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
                  {/* Shared primary links (src/lib/nav.ts). */}
                  {PRIMARY_NAV_VISIBLE.map((link) => (
                    <NavLink key={link.href} href={link.href}>
                      {link.label}
                    </NavLink>
                  ))}
                  <PhotosLink />
                </div>

                <TodayLine />
              </div>

              {/* Social links */}
              <SocialLinks location="footer" />
            </div>

            {/* Copyright and theme toggle */}
            <div className="mt-12 flex items-center justify-between">
              <p className="text-xs text-zinc-500 dark:text-zinc-500">
                &copy; {currentYear()} Arne Noori. All rights reserved.
              </p>

              {/* Only shown on the home page (hidden elsewhere via CSS) — inner
                  pages have the theme toggle in the header. Rendered
                  unconditionally so SSR/client markup match. */}
              <div className="footer-theme-toggle flex items-center">
                <ThemeToggle />
              </div>
            </div>
          </ContainerInner>
        </div>
      </ContainerOuter>
    </footer>
  )
}
